import React from 'react';
import { Tag } from 'lucide-react';
import { FilterButton, FilterDropdown } from '../ui';

interface TagFilterDropdownProps {
  availableTags: string[];
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  isOpen: boolean;
  setDropdownState: (isOpen?: boolean) => void;
  isMobile: boolean;
}

/**
 * Dashboard tag filter dropdown component that allows filtering tasks by tags
 * Used in the Dashboard header to filter dashboard content by task tags
 */
const DashboardTagFilterDropdown: React.FC<TagFilterDropdownProps> = ({
  availableTags,
  selectedTags,
  onTagsChange,
  isOpen,
  setDropdownState,
  isMobile,
}) => {
  // Don't render if there are no tags to filter by
  if (!availableTags || availableTags.length === 0) {
    return null;
  }

  // Create tag options for the dropdown
  const tagOptions = availableTags.map(tag => ({
    id: tag,
    label: tag,
    icon: <Tag size={16} className="text-blue-500" />,
  }));

  // Get button text based on selected tags
  const getButtonText = () => {
    if (selectedTags.length === 0) {
      return 'Tags';
    } else if (selectedTags.length === 1) {
      return `Tag: ${selectedTags[0]}`;
    } else {
      return `Tags (${selectedTags.length})`;
    }
  };

  // Custom button to show in the dropdown using the reusable FilterButton component
  const tagFilterButton = (
    <FilterButton
      isActive={isOpen}
      hasFilters={selectedTags.length > 0}
      onClick={() => setDropdownState(!isOpen)}
      onClear={e => {
        e.stopPropagation();
        onTagsChange([]);
        setDropdownState(false);
      }}
      label={getButtonText()}
      icon={<Tag size={16} className="text-blue-500" />}
      isMobile={isMobile}
      className=""
    />
  );

  return (
    <FilterDropdown
      options={tagOptions}
      selectedValues={selectedTags}
      onChange={onTagsChange}
      onClear={() => onTagsChange([])}
      allowMultiple={true}
      placeholder="Tags"
      headerText="Tags"
      isMobile={isMobile}
      open={isOpen}
      onOpenChange={state => setDropdownState(state)}
      trigger={tagFilterButton}
      showBackdrop={isMobile}
      buttonClassName={isMobile ? '' : 'bg-white hover:border-blue-300'}
    />
  );
};

export default DashboardTagFilterDropdown;
